import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';

import PageLoader from 'modules/common/components/PageLoader';
import EmptyState from 'modules/common/components/EmptyState';
import { Button } from 'modules/common/input';
import refresh from 'assets/images/refresh.png';

import { TAB_OPTS } from '../constants';

function NotificationList({
  activeTab,
  onActiveTab,
  includes,
  isSeeMore,
  isReloading,
  isLoading,
  list,
  filters,
  onMarkAllRead,
  isPostLoading,
  pagination,
  onShowMore,
  renderLayout,
  onRefresh,
}) {
  return (
    <>
      <div className="flex items-center border-b-3 border-gray-200">
        {TAB_OPTS?.map((item, key) => (
          <Button
            // eslint-disable-next-line react/no-array-index-key
            key={key}
            className={cn(
              'p-2 px-3 cursor-pointer text-center relative flex gap-1.5 items-center justify-center hover:bg-gray-100',
              {
                'border-primary-500 border-b-3 mb-[-3px]':
                  activeTab?.value === item?.value,
              }
            )}
            onClick={onActiveTab(item)}
            role="presentation"
            label={
              <>
                <div className="text-[13px] font-bold text-gray-600">
                  {item?.label}
                </div>

                {item?.value === 'unread' && includes?.totalNew > 0 && (
                  <div className="rounded-full bg-primary-500 text-white text-[8px] p-px px-[5px] -mr-0.5">
                    {includes?.totalNew || '-'}
                  </div>
                )}
              </>
            }
            disabled={isPostLoading}
          />
        ))}

        <Button
          className="group p-2 px-3 cursor-pointer text-center relative flex gap-1.5 items-center justify-center hover:bg-gray-100 ml-auto border-b border-transparent hover:border-gray-100"
          onClick={onRefresh}
          role="presentation"
          label={
            <img
              src={refresh}
              className={cn(
                'h-[19px] w-[19px] opacity-30 group-hover:opacity-80 transition-all duration-75 ease-in',
                {
                  'animate-spin': isReloading,
                }
              )}
              alt="refresh"
            />
          }
          disabled={isPostLoading || isReloading}
        />
      </div>

      <div className="overflow-hidden border-b">
        {!isSeeMore && !isReloading && (
          <div
            className={cn(
              'w-full mx-auto transition-all ease-in',
              isLoading && list?.length > 0
                ? 'h-12 -my-2 -mt-1.5 duration-150'
                : 'h-0 duration-200'
            )}
          >
            <PageLoader width="w-full" />
          </div>
        )}
      </div>

      <div className="flex flex-col md:max-h-[590px] flex-1 overflow-y-auto overflow-x-hidden">
        {list?.length > 0 ? (
          <>
            {filters?.new === 1 && list?.length > 1 && (
              <div className="ml-auto py-0.5">
                <Button
                  className="btn font-semibold xs"
                  label="Mark all as read"
                  onClick={onMarkAllRead}
                  isLoading={isPostLoading}
                  disabled={isPostLoading || isLoading}
                  withLoader
                />
              </div>
            )}

            <div className="divide-y">
              {list?.map((item, key) => (
                <React.Fragment
                  // eslint-disable-next-line react/no-array-index-key
                  key={key}
                >
                  {renderLayout(item)}
                </React.Fragment>
              ))}
            </div>
          </>
        ) : (
          <div className="flex flex-col md:min-h-[140px] my-auto">
            <div className="my-auto">
              {isLoading && !isSeeMore && !isReloading ? (
                <div>
                  <PageLoader width="150px" />
                </div>
              ) : (
                <EmptyState
                  imageSize="h-16 w-16"
                  labelSize="!text-xs !font-normal !mt-2"
                  label="No notifications found."
                  contentClassName="!p-2 !py-3.5"
                  containerClassName="mt-1"
                />
              )}
            </div>
          </div>
        )}
      </div>

      {filters?.page < pagination?.total_pages && (
        <div className="border-t flex overflow-hidden">
          {isLoading && isSeeMore ? (
            <div className="h-12 mx-auto -my-1.5">
              <PageLoader width="w-full" />
            </div>
          ) : (
            <Button
              className="btn sm font-semibold !text-xs w-full !py-2.5"
              label="show more"
              onClick={onShowMore}
            />
          )}
        </div>
      )}
    </>
  );
}

NotificationList.defaultProps = {
  list: [],
  filters: {},
  activeTab: {},
  pagination: {},
  isSeeMore: false,
  isLoading: false,
  isReloading: false,
  isPostLoading: false,
  includes: {},
  onActiveTab: () => {},
  onRefresh: () => {},
  renderLayout: () => {},
  onMarkAllRead: () => {},
  onShowMore: () => {},
};

NotificationList.propTypes = {
  isLoading: PropTypes.bool,
  isSeeMore: PropTypes.bool,
  isReloading: PropTypes.bool,
  isPostLoading: PropTypes.bool,
  list: PropTypes.instanceOf(Array),
  filters: PropTypes.instanceOf(Object),
  pagination: PropTypes.instanceOf(Object),
  activeTab: PropTypes.instanceOf(Object),
  includes: PropTypes.instanceOf(Object),
  onActiveTab: PropTypes.instanceOf(Function),
  onRefresh: PropTypes.instanceOf(Function),
  onMarkAllRead: PropTypes.instanceOf(Function),
  onShowMore: PropTypes.instanceOf(Function),
  renderLayout: PropTypes.instanceOf(Function),
};

export default NotificationList;
